import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/_core/hooks/useAuth";
import {
  AlertTriangle, CreditCard, ArrowRight, LogOut, Scissors,
  Check, Star, Zap, Crown, Sparkles, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";

interface SuspendedPageProps {
  reason: "suspended" | "trial_expired" | "cancelled";
  trialEndsAt?: Date | null;
}

const PLAN_ICONS: Record<string, React.ReactNode> = {
  "Essencial": <Zap className="w-5 h-5" />,
  "Profissional": <Star className="w-5 h-5" />,
  "Premium": <Crown className="w-5 h-5" />,
  "Elite": <Sparkles className="w-5 h-5" />,
};

const PLAN_COLORS: Record<string, string> = {
  "Essencial": "from-slate-600 to-slate-700",
  "Profissional": "from-amber-600 to-amber-700",
  "Premium": "from-purple-600 to-purple-700",
  "Elite": "from-rose-600 to-rose-700",
};

const PLAN_BORDER: Record<string, string> = {
  "Essencial": "border-slate-500/40",
  "Profissional": "border-amber-500/40",
  "Premium": "border-purple-500/40",
  "Elite": "border-rose-500/40",
};

const PLAN_BADGE: Record<string, string> = {
  "Essencial": "",
  "Profissional": "Mais popular",
  "Premium": "",
  "Elite": "Completo",
};

export default function SuspendedPage({ reason, trialEndsAt }: SuspendedPageProps) {
  const { logout } = useAuth();
  const [showPlans, setShowPlans] = useState(reason === "trial_expired");
  const [checkingOut, setCheckingOut] = useState<number | null>(null);

  const { data: plans } = trpc.plans.list.useQuery();

  const reactivatePortal = trpc.salon.reactivateBillingPortal.useMutation({
    onSuccess: (data) => {
      if (data?.url) window.location.href = data.url;
    },
    onError: () => toast.error("Erro ao abrir portal de pagamento. Tente novamente."),
  });

  const createCheckout = trpc.salon.createSubscriptionCheckout.useMutation({
    onSuccess: (data) => {
      if (data?.url) {
        toast.info("Redirecionando para o checkout...");
        window.open(data.url, "_blank");
      }
      setCheckingOut(null);
    },
    onError: () => {
      toast.error("Erro ao criar checkout. Tente novamente.");
      setCheckingOut(null);
    },
  });

  const handlePlanCheckout = (planId: number) => {
    setCheckingOut(planId);
    createCheckout.mutate({ planId, origin: window.location.origin });
  };

  const titles: Record<string, string> = {
    suspended: "Assinatura Suspensa",
    trial_expired: "Período de Teste Encerrado",
    cancelled: "Assinatura Cancelada",
  };

  const descriptions: Record<string, string> = {
    suspended: "Sua assinatura foi suspensa por falta de pagamento. Reative agora para continuar usando o sistema.",
    trial_expired: "Seu período de teste gratuito encerrou. Escolha um plano abaixo para continuar gerenciando seu salão.",
    cancelled: "Sua assinatura foi cancelada. Para retomar o acesso, escolha um plano abaixo.",
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-amber-950 flex flex-col items-center justify-start p-4 py-10">
      <div className="w-full max-w-4xl space-y-6">
        {/* Logo */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-amber-400 mb-2">
            <Scissors className="w-6 h-6" />
            <span className="font-bold text-lg">Beleza &amp; Bronze</span>
          </div>
        </div>

        {/* Card de alerta */}
        <div className="max-w-lg mx-auto">
          <div className="rounded-2xl border border-red-500/30 bg-stone-900/80 backdrop-blur p-8 text-center space-y-5">
            {/* Ícone */}
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>

            {/* Título e descrição */}
            <div className="space-y-2">
              <h1 className="text-2xl font-bold text-white">{titles[reason]}</h1>
              <p className="text-stone-400 leading-relaxed">{descriptions[reason]}</p>
              {reason === "trial_expired" && trialEndsAt && (
                <p className="text-sm text-stone-500">
                  Seu trial encerrou em {new Date(trialEndsAt).toLocaleDateString("pt-BR")}
                </p>
              )}
            </div>

            {/* Ações */}
            <div className="space-y-3">
              {reason === "suspended" ? (
                <Button
                  className="w-full bg-amber-600 hover:bg-amber-700 text-white h-12 text-base"
                  onClick={() => reactivatePortal.mutate({ origin: window.location.origin })}
                  disabled={reactivatePortal.isPending}
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  {reactivatePortal.isPending ? "Redirecionando..." : "Reativar pelo Portal Stripe"}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : null}

              <Button
                variant="outline"
                className="w-full border-amber-500/40 text-amber-400 hover:bg-amber-500/10 h-11"
                onClick={() => setShowPlans((v) => !v)}
              >
                {showPlans ? <ChevronUp className="w-4 h-4 mr-2" /> : <ChevronDown className="w-4 h-4 mr-2" />}
                {showPlans ? "Ocultar planos" : "Ver planos e assinar"}
              </Button>

              <Button
                variant="ghost"
                className="w-full text-stone-500 hover:text-white"
                onClick={() => logout()}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Sair da conta
              </Button>
            </div>
          </div>
        </div>

        {/* Comparativo de planos */}
        {showPlans && plans && plans.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-center text-xl font-bold text-white">Escolha seu plano</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {plans.map((plan) => {
                const badge = PLAN_BADGE[plan.name] ?? "";
                const gradient = PLAN_COLORS[plan.name] ?? "from-stone-600 to-stone-700";
                const borderColor = PLAN_BORDER[plan.name] ?? "border-stone-500/40";
                const icon = PLAN_ICONS[plan.name] ?? <Star className="w-5 h-5" />;
                const isPopular = plan.name === "Profissional";

                return (
                  <div
                    key={plan.id}
                    className={`relative rounded-2xl border ${borderColor} bg-stone-900/70 backdrop-blur p-5 flex flex-col gap-4 ${isPopular ? "ring-2 ring-amber-500/50" : ""}`}
                  >
                    {badge && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className={`bg-gradient-to-r ${gradient} text-white border-0 text-xs px-3 py-0.5`}>
                          {badge}
                        </Badge>
                      </div>
                    )}

                    {/* Header do plano */}
                    <div className="space-y-1">
                      <div className={`inline-flex items-center gap-1.5 text-sm font-semibold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>
                        {icon}
                        {plan.name}
                      </div>
                      <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold text-white">
                          R${Number(plan.priceMonthly).toFixed(0)}
                        </span>
                        <span className="text-stone-500 text-sm">/mês</span>
                      </div>
                    </div>

                    {/* Features */}
                    <ul className="space-y-1.5 flex-1">
                      <li className="flex items-center gap-2 text-sm text-stone-300">
                        <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                        Até {plan.maxProfessionals} profissionais
                      </li>
                      <li className="flex items-center gap-2 text-sm text-stone-300">
                        <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                        Agendamento online
                      </li>
                      {plan.hasAiChatbot && (
                        <li className="flex items-center gap-2 text-sm text-stone-300">
                          <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                          IA no WhatsApp
                        </li>
                      )}
                      {plan.hasAdvancedReports && (
                        <li className="flex items-center gap-2 text-sm text-stone-300">
                          <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                          Gestão de comissões
                        </li>
                      )}
                      {plan.hasAdvancedReports && (
                        <li className="flex items-center gap-2 text-sm text-stone-300">
                          <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                          Relatórios avançados
                        </li>
                      )}
                      {plan.hasMediaPromocional && (
                        <li className="flex items-center gap-2 text-sm text-stone-300">
                          <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                          Mídia promocional
                        </li>
                      )}
                    </ul>

                    {/* Botão */}
                    <Button
                      className={`w-full bg-gradient-to-r ${gradient} text-white border-0 hover:opacity-90`}
                      onClick={() => handlePlanCheckout(plan.id)}
                      disabled={checkingOut === plan.id}
                    >
                      {checkingOut === plan.id ? "Aguarde..." : "Assinar agora"}
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                );
              })}
            </div>

            <p className="text-center text-xs text-stone-600">
              Teste com o cartão <span className="font-mono text-stone-500">4242 4242 4242 4242</span> · Cancele quando quiser · Sem multa
            </p>
          </div>
        )}

        <p className="text-center text-xs text-stone-600">
          Precisa de ajuda?{" "}
          <a href="mailto:suporte@belezaebronze.club" className="text-amber-600 hover:underline">
            suporte@belezaebronze.club
          </a>
        </p>
      </div>
    </div>
  );
}
